import { Send, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { SubmitReasons } from './ReportHeaderBar';
import { useReportPdfAction } from './ReportPdf';
import type { Report } from './types';

const REASONS_ID = 'submit-reasons';

/**
 * The note's actions in its header bar (#952 H6): «Invia per approvazione» last and filled, the
 * note's PDF and `lead` (its «Modifica», the approver's «Decidi») before it, and «···» holding
 * «Elimina bozza», set apart from the main action. Each only when `can` allows it. «Invia» stays
 * visible but off while something blocks it; the reasons, each with its step as a link, go in
 * `notices`, under the totals.
 */
export function useReportBar({
  report,
  busy,
  lead,
  onSubmit,
  onDiscard,
}: {
  readonly report: Report;
  readonly busy: boolean;
  readonly lead?: ReactNode;
  readonly onSubmit: () => void;
  readonly onDiscard: () => void;
}): { readonly actions: ReactNode; readonly notices: ReactNode } {
  const { t } = useTranslation('expenses');
  // Round 1 keeps this confirmation: the server soft-deletes a draft but offers no restore, so an
  // undo toast cannot work yet. The undoable delete (U9, U10) is a round-2 item: «Undo for a
  // deleted draft report», which needs a restore endpoint.
  const [confirming, setConfirming] = useState(false);
  const pdf = useReportPdfAction(report);
  const can = report.can ?? {};
  const blockers = can.submit === true ? [] : (report.submitBlockers ?? []);
  const showSubmit = can.submit === true || (can.editExpenses === true && blockers.length > 0);
  const more =
    can.delete === true
      ? [
          {
            id: 'delete',
            label: t('reports.page.delete'),
            icon: Trash2,
            tone: 'destructive' as const,
            busy,
            onSelect: () => setConfirming(true),
          },
        ]
      : [];

  const actions = (
    <div className="flex flex-wrap items-center gap-2" data-testid="report-actions">
      {lead}
      {pdf?.controls}
      {/* #952: «Invia» and «···» wrap together, so «···» never sits alone on a row of its own. */}
      <span className="flex items-center gap-2 empty:hidden">
        {showSubmit ? (
          <Button
            type="button"
            className="min-h-11 @min-[620px]/shell:min-h-[38px]"
            disabled={can.submit !== true || busy}
            aria-describedby={blockers.length > 0 ? REASONS_ID : undefined}
            onClick={onSubmit}
          >
            <Send />
            {t('reports.page.submit')}
          </Button>
        ) : null}
        <RowActionsMenu
          actions={more}
          label={t('reports.page.more')}
          triggerClassName="border-chip-border bg-card size-11 border @min-[620px]/shell:size-[38px]"
        />
      </span>
    </div>
  );

  const notices = (
    <>
      {confirming ? (
        <span
          role="alertdialog"
          aria-label={t('reports.page.delete')}
          className="flex flex-wrap items-center gap-2 text-sm"
        >
          {t('reports.page.deleteAsk')}
          <Button
            type="button"
            variant="destructive"
            className="min-h-11 @min-[620px]/shell:min-h-8"
            size="sm"
            disabled={busy}
            onClick={onDiscard}
          >
            {t('reports.page.deleteConfirm')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11 @min-[620px]/shell:min-h-8"
            size="sm"
            onClick={() => setConfirming(false)}
          >
            {t('reports.new.cancel')}
          </Button>
        </span>
      ) : null}
      <SubmitReasons report={report} blockers={showSubmit ? blockers : []} id={REASONS_ID} />
      {pdf?.messages}
    </>
  );
  return { actions, notices };
}
